/**
* Maps cn status enum names to a neutral PrimeNG `p-tag` severity.
*
* IMPORTANT (Clean-Core / NFR-CleanCore):
*  - Wherever an entity status is rendered through `mvs-widget`, the framework already
*    derives the badge color from the backend enum `uiColor`. This helper is ONLY used
*    for hand-built views (data-quality panel, comparison and trace tables) where no
*    `mvs-widget` is in play.
*  - This is a SEMANTIC mapping (success / warning / danger / info), not a hardcoded
*    business color. No HEX/RGB and no business rates are encoded here.
*
* Status names follow the cn enums (CommissionModelStatusEnum, CommissionExpectationStatusEnum,
* CommissionSimulationRunStatusEnum) and are matched case-insensitively by name.
*/
export type CnTagSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';

const SEVERITY_BY_STATUS: { [key: string]: CnTagSeverity } = {
    // CommissionModelStatusEnum
    ACTIVE: 'success',
    SCENARIO: 'info',
    ARCHIVED: 'contrast',

    // CommissionExpectationStatusEnum
    COMPLETE: 'success',
    PAID: 'success',
    PENDING: 'info',
    INCOMPLETE_BASE: 'warn',
    UNMATCHED: 'danger',
    CANCELLED: 'secondary',

    // CommissionSimulationRunStatusEnum
    SUCCESS: 'success',
    RUNNING: 'info',
    NEW: 'secondary',
    ERROR: 'danger',
};

/**
* Returns a neutral severity for a status name (case-insensitive).
* Falls back to 'secondary' for unknown values so the UI never breaks.
*/
export function cnStatusSeverity(status: string | null | undefined): CnTagSeverity {
    if (!status) {
        return 'secondary';
    }
    return SEVERITY_BY_STATUS[String(status).toUpperCase()] ?? 'secondary';
}

/**
* Inline color style for a hand-built status `p-tag` keyed by status name (case-insensitive).
*
* TM-1000001915 (B17): the PrimeNG `success` severity maps to BLUE in the active KlausPreset
* theme, so an ACTIVE/Aktiv badge rendered via `[severity]="success"` showed blue. To stay
* consistent with the dashboard activity badges (which color from the enum uiColor / uiBackgroundColor
* surface tokens) this maps each status to the matching PrimeNG palette tokens:
*   ACTIVE   -> green  (Aktiv)
*   SCENARIO -> blue   (Szenario)
*   ARCHIVED -> grey   (Archiviert)
* Only theme color tokens (var(--*-200) background / var(--*-800) text) are used — no HEX/RGB and
* no hardcoded business color. Returns undefined for unknown values so the tag keeps its default look.
*/
const COLOR_STYLE_BY_STATUS: { [key: string]: { [style: string]: string } } = {
    ACTIVE: {'background-color': 'var(--green-200)', color: 'var(--green-800)'},
    SCENARIO: {'background-color': 'var(--blue-200)', color: 'var(--blue-800)'},
    ARCHIVED: {'background-color': 'var(--surface-300)', color: 'var(--surface-800)'},
};

export function cnStatusColorStyle(
    status: string | null | undefined
): { [style: string]: string } | undefined {
    if (!status) {
        return undefined;
    }
    return COLOR_STYLE_BY_STATUS[String(status).toUpperCase()];
}
